import React from 'react';
import '../../../../css/entity-style.css';
import { Button, Descriptions } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { Entity, StaffEntityDetailsContainerEntityAddressInfoFieldsFragment, StaffUser } from '../../../../generated';
import { FormatDate } from '../../../../constants';
import { Country } from '../../../../types/country';

interface AddressDetailProps {
  address: StaffEntityDetailsContainerEntityAddressInfoFieldsFragment;
  email: string;
  phoneNumber: string;
  getCountryName: (countryCode: string) => string;
}

const AddressDetail: React.FC<AddressDetailProps> = (props) => {
  return (
    <div>
      <p className="m-0 p-0">Address:</p>
      <p className="m-0 p-0">{props.address.addressLine1}</p>
      <p className="m-0 p-0">{props.address.addressLine2}</p>
      <p className="m-0 p-0">
        {props.address.city}
        {props.address.stateOrProvince && `, ${props.address.stateOrProvince}`} {props.address.zipOrPostalCode}
      </p>
      {props.address.country && <p className="m-0 p-0">{props.getCountryName(props.address.country)}</p>}
      <p className="m-0 p-0">{props.email}</p>
      <p className="m-0 p-0">{props.phoneNumber}</p>
    </div>
  );
};

interface EntityDetailsProps {
  entity: Entity;
  onDeleteEntity: (payload: string) => void;
  deleteLoading: boolean;
  countryData: Country[];
  currentStaffData: StaffUser;
}

export const EntityDetails: React.FC<EntityDetailsProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const entity = props?.entity;
  const canEditEntities = props.currentStaffData?.role?.permissions?.entityPermissions?.canEditEntities;
  const canDeleteEntities = props.currentStaffData?.role?.permissions?.entityPermissions?.canDeleteEntities;

  const getCountryName = (countryCode: string) => {
    return props?.countryData?.find((country) => country.countryCode === countryCode)?.countryName ?? ' ';
  };

  const handleEdit = () => {
    const newPath = location.pathname.replace('detail', 'edit');
    navigate(newPath);
  };

  const handleDelete = () => {
    props?.onDeleteEntity(entity?.id);
  };

  const metadataItems = [
    { label: 'Entity Name', value: entity?.entityName ?? '' },
    { label: 'Id', value: entity?.id ?? '' },
    { label: 'Created At', value: FormatDate(entity?.createdAt) },
    entity?.updatedAt && {
      label: 'Updated At',
      value: FormatDate(entity?.updatedAt)
    }
  ].filter(Boolean);

  return (
    <div>
      <div className="flex justify-between self-center w-full mb-5">
        <div className="ml-auto -mt-[70px]">
            <Button
              loading={props?.deleteLoading}
              disabled={!canDeleteEntities}
              className="text-red-500 border-red-500 mx-2 w-24"
              onClick={handleDelete}
            >
              Delete
            </Button>
            <Button
              disabled={props.deleteLoading || !canEditEntities}
              type="primary"
              className="w-24"
              onClick={handleEdit}
            >
              Edit
            </Button>
        </div>
      </div>

      <div className="p-[20px] mb-2">
        <Descriptions layout="vertical" column={metadataItems.length}>
          {metadataItems.map((item) => (
            <Descriptions.Item key={item.label} label={item.label}>
              {item.value}
            </Descriptions.Item>
          ))}
          <Descriptions.Item label="Address">
            {entity?.address?.city}, {getCountryName(entity?.address?.country ?? '')}
          </Descriptions.Item>
        </Descriptions>

        <div className="flex items-start justify-start flex-col mt-10 p-2">
          <div className="mb-7">
            <div>
              <p>
                Is Client: <span className="font-extrabold mt-[30px]">{entity?.isClient ? 'YES' : 'NO'}</span>
              </p>
            </div>
            <div className="ml-[50px] flex items-start flex-col">
              {entity?.isClient ? (
                <AddressDetail address={entity?.client?.address ?? {}} email={entity?.client?.email ?? ''} phoneNumber={entity?.client?.phoneNumber ?? ''} getCountryName={getCountryName}/>
              ) : null}
            </div>
          </div>
          <div className="mb-7">
            <div>
              <p>
                Is Issuing Institution: <span className="font-extrabold mt-[30px]">{entity?.isIssuingInstitution ? 'YES' : 'NO'}</span>
              </p>
            </div>
            <div className="ml-[50px] flex items-start flex-col">
              {entity?.isIssuingInstitution ? (
                <AddressDetail
                  address={entity?.issuingInstitution?.address ?? {}}
                  email={entity?.issuingInstitution?.email ?? ''}
                  phoneNumber={entity?.issuingInstitution?.phoneNumber ?? ''}
                  getCountryName={getCountryName}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
